import { Component, HostListener } from "@angular/core";
import { ColDef, ExcelStyle, GridApi, GridOptions , GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { PlansColumnDefinition } from "./ag-grid-column-definition";
import { ModalService } from "src/app/shared/modal.service";
import { PlanOnboardingModalOptions } from "./modals/plan-onboarding-modal/plan-onboarding-modal.component";
import { Plan } from "src/app/generated-models/Plan";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { SharedDataService } from "../../services/sharedDataService";
import { exportExcel, getExportedExcelFileNameSuffix, saveFile } from "src/app/utility/common-helper";
import { ColParams } from "src/app/shared/grid/ag-grid";
import * as CDs from "src/app/utility/commonColDef";
import { PlanOnboard } from "src/app/generated-models/PlanOnboard";
import { BlueprintType } from "src/app/npr-request.model";

@Component({
  standalone: false,
  templateUrl: "./plans.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})
export class PlansComponent extends GridViewBaseComponent {
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;
  showMyServiceTeamPlansOnly: boolean = true;
  myPlanServiceTreeIds: string[] = [];
  rowData: Plan[] = [];
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  keyFilterStats = "filter:region-agnostic-plans";
  message: string;
  isAdmin = false;

  constructor(
    protected apiService: ApiService,
    private modalService: ModalService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    this.routeToPlanEditPage();
    super.InitAgGrid(PlansColumnDefinition);

    var plans = await this.apiService.getMyPlans().toPromise();
    if (plans != null) {
      this.myPlanServiceTreeIds = plans.map((plan) => plan.ServiceTreeId);
    }

    if (this.userProfile?.IsApprover) {
      this.showMyServiceTeamPlansOnly = false;
    }

    if (this.userProfile?.IsAdmin) {
      this.isAdmin = true;
    }

    PlansColumnDefinition.forEach((colDef: ColDef) => {
      if (colDef.headerName === "Custom Filter") {
        colDef.valueGetter = (params: ColParams<Plan, void>) => {
          if (!this.showMyServiceTeamPlansOnly) {
            return true;
          }
          const row = params.data as Plan;
          return this.myPlanServiceTreeIds.some((service) => row.ServiceTreeId === service);
        };
      }
    });

    this.gridApi?.setGridOption('columnDefs', PlansColumnDefinition);
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  refreshData() {
    this.loadingService.setLoading(true);
    this.apiService.getPlans().subscribe(
      (response) => {
        this.rowData = response;
        if (this.gridApi) {
          this.gridApi.setGridOption("rowData", this.rowData);
        }
        this.loadingService.setLoading(false);
        super.setupFilterByLocalData();
      },
      (e: any) => {
        this.rowData = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => {
      //this.gridApi.sizeColumnsToFit();
      this.gridApi.autoSizeAllColumns();
    }, 100);
  }

  onShowMyServiceTeamPlansOnlyChanged() {
    this.gridApi.onFilterChanged();
  }

  async showOnboardingDialog() {
    const options: PlanOnboardingModalOptions = {
      save: this.onBoardNewPlan.bind(this),
      type: BlueprintType.PlannedQuota,
      teams: this.rowData.map(p => p.ServiceTreeId),
    };
    await this.modalService.planOnboardingModal(options);
  }

  onBoardNewPlan(serviceTreeId: string, payload: PlanOnboard) {
    this.loadingService.setLoading(true);

    this.apiService.onboardNewPlan(serviceTreeId, payload).subscribe(
      (res: any) => {
        this.message = res?.Message || `New Plan has been onboarded successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err: unknown) => {
        this.message = `Failed to onboard new plan. ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      () => {
        this.refreshData();
      }
    );
  }

  exportExcel() {
    const fileName = "ServiceBlueprints-" + getExportedExcelFileNameSuffix();
    const sheetName = "Service Blueprints";

    this.loadingService.setLoading(true);
    exportExcel(this.gridApi, fileName, sheetName);
    this.loadingService.setLoading(false);
  }

  routeToPlanEditPage() {
    // deep link to edit plan page if service query param is provided
    this.route.queryParams.subscribe((params) => {
      if (params.service && params.service.trim() != "") {
        this.router.navigate([`/plans/services/${params.service}/editor`]);
      }
    });
  }
}
